// src/pages/projects/ProjectDocuments.jsx

import { useEffect, useState, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useParams, useNavigate } from "react-router-dom";
import {
  uploadProjectDocument,
  fetchProjectDocuments,
  updateDocumentStatus,
  DOC_TYPES,
  DOC_STATUS,
} from "./projectDocumentService";
import { canAccessProject, isManagerRole } from "./projectAccess";

// embedded: true when rendered as a tab inside ProjectDetail.
export default function ProjectDocuments({ embedded = false }) {
  const { id }   = useParams();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  // Field roles may upload (drawings come from site) but only office roles
  // approve/reject a document.
  const canApprove = isManagerRole(user.role);

  const [project,   setProject]   = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);

  // Upload form
  const [showUpload,    setShowUpload]    = useState(false);
  const [uploadForm,    setUploadForm]    = useState({
    document_type:     "drawing_shop",
    document_name:     "",
    checklist_item_no: "",
    remarks:           "",
  });
  const [selectedFile,  setSelectedFile]  = useState(null);
  const fileRef = useRef(null);

  // Filter
  const [filterType,   setFilterType]   = useState("all");
  const [filterLatest, setFilterLatest] = useState(true);

  // Version history modal
  const [historyDoc,   setHistoryDoc]   = useState(null);

  // loadDocuments() runs only after fetchProject() passes the access check.
  useEffect(() => { if (id) fetchProject(); }, [id]); // eslint-disable-line

  const fetchProject = async () => {
    try {
      const snap = await getDoc(doc(db, "projects", id));
      if (snap.exists()) {
        const data = snap.data();
        // Company + assignment check (shared rule — projectAccess.js).
        // Also closes direct-URL access to /projects/:id/documents.
        if (!(await canAccessProject(data, snap.id, user))) {
          alert("Access denied — you are not assigned to this project");
          navigate("/projects");
          return;
        }
        setProject({ id: snap.id, ...data });
        loadDocuments();
      } else {
        setLoading(false);
      }
    } catch (err) { console.error(err); setLoading(false); }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const docs = await fetchProjectDocuments(id, company_id);
      setDocuments(docs);
    } catch (err) {
      console.error("loadDocuments error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile)              { alert("Please select a file"); return; }
    if (!uploadForm.document_type)  { alert("Please select a document type"); return; }

    try {
      setUploading(true);
      await uploadProjectDocument({
        file:              selectedFile,
        company_id,
        project_id:        id,
        project_name:      project?.project_name || "",
        document_type:     uploadForm.document_type,
        document_name:     uploadForm.document_name.trim() || "",
        checklist_item_no: uploadForm.checklist_item_no
          ? Number(uploadForm.checklist_item_no) : null,
        remarks:           uploadForm.remarks.trim(),
        uploaded_by:       user.uid || user.id || "",
        uploaded_by_name:  user.name || "",
      });
      alert("Document uploaded ✅");
      setShowUpload(false);
      setSelectedFile(null);
      setUploadForm({ document_type: "drawing_shop", document_name: "", checklist_item_no: "", remarks: "" });
      if (fileRef.current) fileRef.current.value = "";
      loadDocuments();
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleStatusChange = async (docId, newStatus) => {
    try {
      await updateDocumentStatus(docId, newStatus, user);
      loadDocuments();
    } catch (err) {
      alert("Status update failed");
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fmtDate = (ts) => {
    if (!ts) return "—";
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric"
    });
    return "—";
  };

  // Filter docs
  const filtered = documents.filter(d => {
    const matchType   = filterType === "all" || d.document_type === filterType;
    const matchLatest = !filterLatest || d.is_latest === true;
    return matchType && matchLatest;
  });

  // Group by type for summary
  const typeCounts = Object.keys(DOC_TYPES).reduce((acc, key) => {
    acc[key] = documents.filter(d => d.document_type === key && d.is_latest).length;
    return acc;
  }, {});

  // Version history — all versions of same type
  const getVersionHistory = (docItem) => {
    return documents
      .filter(d => d.document_type === docItem.document_type)
      .sort((a, b) => b.version - a.version);
  };

  if (loading) return <div className="p-6 text-gray-500">Loading documents...</div>;

  return (
    <div className={embedded ? "space-y-5" : "p-4 md:p-6 space-y-5 max-w-5xl mx-auto"}>

      {/* HEADER */}
      <div className="flex justify-between items-start">
        <div>
          {!embedded && (
            <button onClick={() => navigate(`/projects/${id}`)}
              className="text-gray-400 text-sm hover:text-gray-600 mb-1 block">
              ← {project?.project_name || "Project"}
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-800">Documents</h1>
          <p className="text-sm text-gray-500">{project?.client_name}</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">
          + Upload Document
        </button>
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
        {Object.entries(DOC_TYPES).map(([key, val]) => (
          typeCounts[key] > 0 && (
            <div key={key}
              onClick={() => setFilterType(filterType === key ? "all" : key)}
              className={`rounded-xl p-3 cursor-pointer transition border ${
                filterType === key
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-100 bg-white hover:bg-gray-50"
              }`}>
              <div className="text-lg">{val.icon}</div>
              <div className="text-xs font-semibold text-gray-700 mt-1 leading-tight">
                {val.label}
              </div>
              <div className="text-lg font-bold text-gray-800">{typeCounts[key]}</div>
            </div>
          )
        ))}
      </div>

      {/* FILTERS */}
      <div className="flex gap-3 items-center flex-wrap">
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="border p-2 rounded-xl text-sm outline-none bg-white">
          <option value="all">All Types</option>
          {Object.entries(DOC_TYPES).map(([key, val]) => (
            <option key={key} value={key}>{val.icon} {val.label}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={filterLatest}
            onChange={e => setFilterLatest(e.target.checked)}
            className="accent-blue-600"
          />
          Latest versions only
        </label>

        <span className="text-xs text-gray-400 ml-auto">
          {filtered.length} document{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* DOCUMENT LIST */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📁</div>
          <p className="font-semibold">No documents yet</p>
          <p className="text-sm mt-1">Upload drawings, BOQ, work orders</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(d => {
            const typeInfo   = DOC_TYPES[d.document_type]   || DOC_TYPES.other;
            const statusInfo = DOC_STATUS[d.status]         || DOC_STATUS.draft;
            const versions   = getVersionHistory(d);

            return (
              <div key={d.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-start gap-3">

                  {/* Type icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 ${typeInfo.color}`}>
                    {typeInfo.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800 text-sm">
                        {d.document_name}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                      {!d.is_latest && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-400 rounded-full text-xs">
                          Old version
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 flex-wrap">
                      <span className="font-semibold text-blue-600">{d.version_label}</span>
                      <span>{d.file_name}</span>
                      <span>{fmtSize(d.file_size)}</span>
                      <span>{fmtDate(d.created_at)}</span>
                      <span>by {d.uploaded_by_name || "—"}</span>
                      {d.checklist_item_no && (
                        <span className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
                          Checklist #{d.checklist_item_no}
                        </span>
                      )}
                    </div>

                    {d.remarks && (
                      <p className="text-xs text-gray-400 mt-1 italic">"{d.remarks}"</p>
                    )}

                    {/* Version history badge */}
                    {versions.length > 1 && d.is_latest && (
                      <button
                        onClick={() => setHistoryDoc(historyDoc?.id === d.id ? null : d)}
                        className="mt-1.5 text-xs text-blue-500 hover:underline">
                        {versions.length} versions — view history
                      </button>
                    )}

                    {/* Version history expanded */}
                    {historyDoc?.id === d.id && (
                      <div className="mt-2 border border-gray-100 rounded-xl overflow-hidden">
                        {versions.map((v, i) => (
                          <div key={v.id}
                            className={`flex items-center gap-3 px-3 py-2 text-xs border-b border-gray-50 last:border-0 ${
                              i === 0 ? "bg-blue-50" : "bg-white"
                            }`}>
                            <span className="font-semibold text-blue-600 w-6">{v.version_label}</span>
                            <span className="text-gray-500 flex-1">{v.file_name}</span>
                            <span className="text-gray-400">{fmtDate(v.created_at)}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              DOC_STATUS[v.status]?.color || ""
                            }`}>{DOC_STATUS[v.status]?.label}</span>
                            <a href={v.file_url} target="_blank" rel="noreferrer"
                              className="text-blue-500 hover:underline">
                              Download
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 shrink-0">
                    <a href={d.file_url} target="_blank" rel="noreferrer"
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">
                      View
                    </a>
                    {canApprove && d.status === "submitted" && (
                      <>
                        <button
                          onClick={() => handleStatusChange(d.id, "approved")}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700">
                          Approve
                        </button>
                        <button
                          onClick={() => handleStatusChange(d.id, "rejected")}
                          className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-200">
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* UPLOAD MODAL */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-bold text-gray-800 text-lg">Upload Document</h3>
              <button onClick={() => { setShowUpload(false); setSelectedFile(null); }}
                className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>

            <div className="space-y-3">
              {/* Document Type */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  Document Type *
                </label>
                <select
                  value={uploadForm.document_type}
                  onChange={e => setUploadForm(f => ({ ...f, document_type: e.target.value }))}
                  className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
                  {Object.entries(DOC_TYPES).map(([key, val]) => (
                    <option key={key} value={key}>{val.icon} {val.label}</option>
                  ))}
                </select>
              </div>

              {/* Document Name */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  Document Name <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  placeholder="e.g. Shop Drawing - Ground Floor"
                  value={uploadForm.document_name}
                  onChange={e => setUploadForm(f => ({ ...f, document_name: e.target.value }))}
                  className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              {/* Checklist Item */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  Link to Checklist Item # <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="number"
                  placeholder="e.g. 6 (Shop Drawing item)"
                  value={uploadForm.checklist_item_no}
                  onChange={e => setUploadForm(f => ({ ...f, checklist_item_no: e.target.value }))}
                  className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              {/* Remarks */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  Remarks <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  placeholder="e.g. v2 - Client revision changes"
                  value={uploadForm.remarks}
                  onChange={e => setUploadForm(f => ({ ...f, remarks: e.target.value }))}
                  className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  File * <span className="font-normal text-gray-400">(PDF, DWG, DXF, JPG, PNG, XLSX)</span>
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                  onChange={e => setSelectedFile(e.target.files[0] || null)}
                  className="w-full border border-dashed border-blue-300 rounded-xl p-3 text-sm text-gray-600"
                />
                {selectedFile && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ {selectedFile.name} ({fmtSize(selectedFile.size)})
                  </p>
                )}
              </div>

              {/* Version info */}
              {uploadForm.document_type && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <p className="text-xs text-blue-700">
                    {documents.filter(d =>
                      d.document_type === uploadForm.document_type && d.is_latest
                    ).length > 0
                      ? `⚠️ Existing ${DOC_TYPES[uploadForm.document_type]?.label} found — new upload will create next version automatically`
                      : `✅ First ${DOC_TYPES[uploadForm.document_type]?.label} — will be saved as v1`
                    }
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => { setShowUpload(false); setSelectedFile(null); }}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-600 font-semibold text-sm">
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-50">
                {uploading ? "Uploading..." : "Upload ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}